import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import WorldMap from '../components/gallery/WorldMap';
import ImageModal from '../components/gallery/ImageModal';
import GalleryViewTabs, { GalleryTab } from '../components/gallery/GalleryViewTabs';
import PhotosSection from '../components/gallery/PhotosSection';
import VideosSection from '../components/gallery/VideosSection';
import { getCountryCoordinates } from '../data/countryCoordinates';
import { fetchGalleryImages, GalleryImage } from '../services/galleryService';
import { Globe } from 'lucide-react';

interface CountryPin {
  country: string;
  latitude: number;
  longitude: number;
  year: string;
}

interface CountryGallery {
  country: string;
  images: GalleryImage[];
}

const GalleryPage: React.FC = () => {
  const [countryGalleries, setCountryGalleries] = useState<CountryGallery[]>([]);
  const [countryPins, setCountryPins] = useState<CountryPin[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [allImages, setAllImages] = useState<GalleryImage[]>([]);
  const [videos, setVideos] = useState<GalleryImage[]>([]);
  const [activeTab, setActiveTab] = useState<GalleryTab>('photos');

  useEffect(() => {
    const loadGalleryImages = async () => {
      try {
        const images = await fetchGalleryImages();
        setAllImages(images);

        // Group images by country
        const groupedByCountry = images.reduce((acc, image) => {
          const existing = acc.find((g) => g.country === image.country);
          if (existing) {
            existing.images.push(image);
          } else {
            acc.push({ country: image.country, images: [image] });
          }
          return acc;
        }, [] as CountryGallery[]);

        setCountryGalleries(groupedByCountry);

        // Filter images with YouTube URLs for video gallery
        const videosWithYouTube = images.filter(img => img.youtubeUrl && img.youtubeUrl.trim() !== '');
        setVideos(videosWithYouTube);

        // Create pins for countries with coordinates
        const pins: CountryPin[] = groupedByCountry.map((gallery) => {
          const coords = getCountryCoordinates(gallery.country);
          // Get the most recent year for this country
          const mostRecentYear = gallery.images
            .map((img) => img.year)
            .sort()
            .reverse()[0] || '';
          
          return {
            country: gallery.country,
            latitude: coords?.latitude || 0,
            longitude: coords?.longitude || 0,
            year: mostRecentYear,
          };
        }).filter(pin => pin.year); // Only include pins with valid year data

        setCountryPins(pins);
        setLoading(false);
      } catch (e) {
        console.error('Failed to load gallery images:', e);
        setLoading(false);
      }
    };

    loadGalleryImages();
  }, []);

  const handleCountryClick = (country: string) => {
    setSelectedCountry(country);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedCountry(null), 300);
  };

  const selectedCountryGallery = selectedCountry
    ? countryGalleries.find((g) => g.country === selectedCountry)
    : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-gallery-light">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600 dark:text-slate-400 font-medium">
            Loading gallery...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-gallery-page py-20">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header Section */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Globe className="w-10 h-10 text-amber-500" />
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 dark:text-white font-serif">
              Travel Gallery
            </h1>
          </div>
          <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
            Explore our journey across the globe — browse photos, watch videos,
            or discover destinations on the map.
          </p>
        </motion.div>

        {/* View Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <GalleryViewTabs activeTab={activeTab} onChange={setActiveTab} />
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'map' && (
            <motion.div
              key="map"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.4 }}
            >
              <WorldMap countries={countryPins} onCountryClick={handleCountryClick} />
            </motion.div>
          )}

          {activeTab === 'photos' && (
            <motion.div
              key="photos"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
            >
              <PhotosSection images={allImages} />
            </motion.div>
          )}

          {activeTab === 'videos' && (
            <motion.div
              key="videos"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
            >
              <VideosSection videos={videos} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Call to Action */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <div className="gradient-amber rounded-2xl p-8 shadow-2xl">
            <h3 className="text-3xl font-bold text-white mb-3 font-serif">
              Ready for Your Adventure?
            </h3>
            <p className="text-white/90 text-lg mb-6 max-w-2xl mx-auto">
              Join us on an unforgettable journey to discover the world's most beautiful destinations.
            </p>
            <button
              onClick={() => window.location.href = '/tours'}
              className="bg-white text-amber-600 px-8 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all shadow-lg hover:shadow-xl"
            >
              Explore Our Tours
            </button>
          </div>
        </motion.div>
      </div>

      {/* Image Modal (map view) */}
      {selectedCountry && selectedCountryGallery && (
        <ImageModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          country={selectedCountry}
          images={selectedCountryGallery.images}
        />
      )}
    </div>
  );
};

export default GalleryPage;
